import { useEffect, useState } from "react";
import { proxies } from "../lib/api";
import type { Proxy, ProxyStats } from "../lib/api";
import { Trash2, RefreshCw, Globe, Upload, CheckCircle, XCircle } from "lucide-react";

export default function Proxies() {
  const [proxyList, setProxyList] = useState<Proxy[]>([]);
  const [stats, setStats] = useState<ProxyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [testing, setTesting] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const data = await proxies.list();
      setProxyList(data.proxies);
      setStats(data.stats);
    } catch (error) {
      console.error("Failed to fetch proxies", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleBulkImport = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await proxies.bulkImport(bulkText);
      alert(`Imported ${result.imported} proxies`);
      setShowBulk(false);
      setBulkText("");
      fetchData();
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      const result = await proxies.test(id);
      if (result.success) {
        alert(`Proxy OK! IP: ${result.ip}, Latency: ${result.latency}ms`);
      } else {
        alert("Proxy failed test");
      }
      fetchData();
    } catch (error) {
      alert("Test failed: " + (error as Error).message);
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this proxy?")) return;
    try {
      await proxies.delete(id);
      fetchData();
    } catch (error) {
      console.error("Failed to delete proxy", error);
    }
  };

  const handleClearAll = async () => {
    if (!confirm("Delete ALL proxies?")) return;
    try {
      await proxies.clear();
      fetchData();
    } catch (error) {
      console.error("Failed to clear proxies", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card">
            <p className="text-gray-400 text-sm">Total Proxies</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="card">
            <p className="text-gray-400 text-sm">Healthy</p>
            <p className="text-2xl font-bold text-success">{stats.healthy}</p>
          </div>
          <div className="card">
            <p className="text-gray-400 text-sm">Unhealthy</p>
            <p className="text-2xl font-bold text-error">{stats.unhealthy}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Proxies</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowBulk(true)} className="btn-primary flex items-center gap-2">
            <Upload size={18} />
            Bulk Import
          </button>
          <button onClick={handleClearAll} className="btn-danger flex items-center gap-2">
            <Trash2 size={18} />
            Clear All
          </button>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full">
            <h3 className="font-display text-lg font-semibold mb-4">Bulk Import Proxies</h3>
            <form onSubmit={handleBulkImport} className="space-y-4">
              <div>
                <label className="label">Proxy List (one per line)</label>
                <textarea
                  className="input h-48 font-mono text-sm"
                  placeholder="host:port
host:port:user:pass
http://user:pass@host:port"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowBulk(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Import</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Proxy List */}
      {proxyList.length === 0 ? (
        <div className="card text-center py-12">
          <Globe size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No proxies yet. Import some to get started!</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Host</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Port</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Protocol</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Fails</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {proxyList.map((proxy) => (
                <tr key={proxy.id} className="hover:bg-dark-700/50">
                  <td className="px-4 py-3 font-mono text-sm">{proxy.host}</td>
                  <td className="px-4 py-3">{proxy.port}</td>
                  <td className="px-4 py-3 text-sm">{proxy.protocol}</td>
                  <td className="px-4 py-3">
                    {proxy.isHealthy ? (
                      <span className="flex items-center gap-1 text-success text-sm">
                        <CheckCircle size={14} /> Healthy
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-error text-sm">
                        <XCircle size={14} /> Unhealthy
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{proxy.failCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleTest(proxy.id)}
                        disabled={testing === proxy.id}
                        className="p-2 hover:bg-dark-600 rounded transition-colors"
                      >
                        {testing === proxy.id ? (
                          <RefreshCw size={16} className="animate-spin text-primary-500" />
                        ) : (
                          <RefreshCw size={16} className="text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(proxy.id)}
                        className="p-2 hover:bg-dark-600 rounded transition-colors"
                      >
                        <Trash2 size={16} className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

